import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Key } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PasswordResetModal = ({ isOpen, onClose }: PasswordResetModalProps) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: t('auth.passwordsDoNotMatch'),
        description: t('auth.passwordsMatchError'),
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        variant: "destructive",
        title: t('auth.passwordTooShort'),
        description: t('auth.passwordMinLength'),
      });
      return;
    }

    setIsResetting(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw error;
      }

      setResetSuccess(true);
      toast({
        title: t('auth.passwordUpdatedSuccessfully'),
        description: t('auth.passwordResetComplete'),
      });
    } catch (error: any) {
      console.error("Error resetting password:", error);
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: error.message || t('auth.failedToResetPassword'),
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleClose = () => {
    setResetSuccess(false);
    setNewPassword("");
    setConfirmPassword("");
    onClose();
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleClose}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl">
            {resetSuccess ? t('auth.passwordResetSuccessful') : t('auth.resetYourPassword')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {resetSuccess 
              ? t('auth.passwordResetSuccessfully')
              : t('auth.enterNewPassword')
            }
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!resetSuccess ? (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
              <div className="relative">
                <Key className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="new-password"
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
              <div className="relative">
                <Key className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <AlertDialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isResetting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleResetPassword}
                className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90"
                disabled={isResetting}
              >
                {isResetting ? (
                  <div className="flex items-center gap-2">
                    <img 
                      src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                      alt={t('common.loading')} 
                      className="h-4 w-4 gebeya-spinner" 
                    />
                    <span>{t('auth.resetting')}</span>
                  </div>
                ) : t('auth.resetPasswordButton')}
              </Button>
            </AlertDialogFooter>
          </div>
        ) : (
          <AlertDialogFooter>
            <AlertDialogAction
              className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90"
              onClick={handleClose}
            >
              {t('common.close')}
            </AlertDialogAction>
          </AlertDialogFooter>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}; 